import Transient from '@components/layouts/transient';
import NavTab from '@components/layouts/nav-tab';
import { store } from '@store';
import SymfonyFrame from '@components/layouts/symfony-frame';
import { getBasicText } from '@common/text/text';
import { axiosApi } from '@models/axios';

const editTab = (tab, component, meta = {}) => {
  meta.titleI18n = `source.${tab}.title`;
  meta.breadcrumb = () => [
    { i18nLabel: 'source.title', routeName: 'source-overview' },
    { integrationDropdown: 'source' },
  ];
  return {
    path: tab,
    name: `source-${tab}`,
    props: route => {
      const props = { ...route.params };
      props.id = Number(props.id);
      props.step = tab;
      props.integrationId = Number(props.id);

      return props;
    },
    component: component ? component : () => import('@views/integration/source/source.vue'),
    meta,
  };
};
const hideTab = tab => {
  switch (tab) {
    case 'input':
      return store.state.integration.integration.entity.connector?.template !== 'cax_data_store_reply_event';
    case 'fileSettings':
    case 'mapping':
      return !store.state.integration.integration.entity.connector?.categories?.some(
        category => category.label === 'File import'
      );
    case 'filters':
      return store.state.integration.integration.entity.connector?.template !== 'cax_data_store_reply_event';
    case 'activation':
      return (
        store.state.integration.integration.entity.connector?.template !== 'cax_data_store_reply_event' &&
        !store.state.integration.integration.entity.connector?.categories?.some(
          category => category.label === 'File import'
        )
      );
    case 'quality':
      return store.state.integration.integration.entity.connector?.template === 'cax_data_store_reply_event';
    case 'inspector':
      return store.state.integration.integration.entity.connector?.template === 'cax_data_store_reply_event';
    case 'get-code':
      return true;
    default:
      return false;
  }
};

export const source = {
  path: 'sources/',
  name: 'sources',
  component: Transient,
  meta: {
    breadcrumb: null,
  },
  children: [
    {
      path: '',
      name: 'source-overview',
      component: () => import('@views/integration/sources-overview.vue'),
      props: {
        overviewType: 'source',
      },
      meta: {
        breadcrumb: () => [{ i18nLabel: 'source.title', routeName: 'source-overview' }],
        can: 'view.Tms|view.DmsStream',
      },
    },
    {
      path: 'containers/',
      name: 'containers-parent',
      component: Transient,
      meta: {
        breadcrumb: null,
      },
      children: [
        {
          path: '',
          name: 'containers',
          component: () => import('@views/containers/containers-overview.vue'),
          meta: {
            breadcrumb: () => [
              { i18nLabel: 'menuItems.integrations.sources.list.containers', routeName: 'containers' },
            ],
          },
        },
        {
          path: ':containerID(\\d+)?/',
          name: 'container',
          async beforeEnter(to, from, next) {
            await Promise.all([
              // find all containers so that you can match the container id to the url and show it's name on the side nav
              store.dispatch('tms/webContainer/findAllCached', {
                include: 'deployment_mailing_list',
              }),
              // find integration so you can match an integration to show on the breadcrumb
              store.dispatch('integration/integration/findAllCached', {
                include: 'connector,model',
                filter: {
                  types: 'source',
                },
              }),
              // find all tags so that the first tag can be used to navigate to the edit page
              store.dispatch('tms/webTag/findAll', {
                filter: { container: to.params.containerID },
              }),
            ]);

            let integration = store.state.integration.integration.entities.find(
              integration => integration?.model?.id === to.params.containerID
            );

            // fetch integrations if needed
            if (integration == null) {
              const integrations = await store.dispatch('integration/integration/findAll', {
                include: 'connector,model',
                filter: {
                  types: 'source',
                  templates: 'containers',
                },
                'with-branches': true,
              });
              integration = integrations.data.find(integration => integration.model?.id === to.params.containerID);
            }
            await store.dispatch('tms/webContainer/loadCurrent', to.params.containerID);
            store.dispatch('integration/integration/loadCurrent', {
              id: integration.id,
            }),
              next();
          },
          children: [
            {
              path: 'edit/:tagID?',
              name: 'web-containers-edit',
              component: SymfonyFrame,
              props: false,
              meta: {
                symfonyPath: to => {
                  const { containerID, tagID } = to.params;
                  const firstTag = store.getters['tms/webTag/getFirstActiveOrAvailableTag'](containerID);
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  const symfonyPath = tagID
                    ? `/containers/edit/${containerID}/${containerName}/tag/${tagID}`
                    : firstTag
                    ? `/containers/edit/${containerID}/${containerName}/tag/${firstTag.id}`
                    : `/containers/generate/${containerID}/${containerName}`;
                  return symfonyPath;
                },
              },
            },
            {
              path: 'test/',
              name: 'web-containers-test',
              component: SymfonyFrame,
              props: false,
              meta: {
                can: 'view.TmsTest',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/test/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'modification-history/',
              name: 'tag-modification-history',
              component: SymfonyFrame,
              meta: {
                symfonyPath: '/containers/modification-history',
              },
            },
            {
              path: 'generate/',
              name: 'tag-container-generate',
              component: SymfonyFrame,
              meta: {
                can: 'view.TmsGenerate',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/generate/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'deploy/',
              name: 'tag-container-deploy',
              component: SymfonyFrame,
              meta: {
                can: 'view.TmsDeploy',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/deploy/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'tag-data-sharing/',
              name: 'source-tag-data-sharing',
              component: SymfonyFrame,
              meta: {
                symfonyPath: '/containers/report/tagssummary',
                can: 'edit.TmsContainer',
              },
            },
            {
              path: 'web-container-stats',
              name: 'tag-web-container-stats',
              component: SymfonyFrame,
              meta: {
                symfonyPath: to => {
                  if (to.params.containerID) {
                    return `/containers/summary/${to.params.containerID}/stats`;
                  } else {
                    return '/containers/summary';
                  }
                },
              },
            },
            {
              path: 'tagperf/',
              name: 'tag-tags-performance',
              component: SymfonyFrame,
              meta: {
                symfonyPath: '/containers/tagperf',
              },
            },
            {
              path: 'piggybacking/',
              name: 'tag-piggybacking',
              component: SymfonyFrame,
              meta: {
                symfonyPath: '/containers/tagperf/tags-hierarchy',
              },
            },
            {
              path: 'tags/',
              name: 'tags-overview',
              component: () => import('@views/containers/tags-overview.vue'),
              meta: {
                breadcrumb: () => [
                  { i18nLabel: 'menuItems.containers.tagOverview', routeName: 'tags-overview' },
                  { integrationDropdown: 'container' },
                  { changeBranch: true },
                ],
              },
            },
            {
              path: 'tags-catalog/',
              name: 'tags-catalog',
              component: () => import('@views/containers/tags-catalog.vue'),
              meta: {
                can: 'create.TmsTag',
                breadcrumb: () => [
                  { i18nLabel: 'menuItems.containers.tagCatalog' },
                  { integrationDropdown: 'container' },
                  { changeBranch: true },
                ],
              },
            },
            {
              path: 'rules-summary/',
              name: 'tag-rules-summary',
              component: SymfonyFrame,
              meta: {
                can: 'view.TmsRule',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/rules/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'custom-js/',
              name: 'tag-custom-js',
              component: SymfonyFrame,
              meta: {
                can: 'edit.TmsTag',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/edit/${containerID}/${containerName}/custom-js/1`;
                },
              },
            },
            {
              path: 'triggers/',
              name: 'tag-triggers',
              component: SymfonyFrame,
              meta: {
                can: 'view.TmsRule',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/rules/triggers/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'perimeters/',
              name: 'tag-perimeters',
              component: SymfonyFrame,
              meta: {
                can: 'view.TmsRule',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/rules/perimeters/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'constraints/',
              name: 'tag-constraints',
              component: SymfonyFrame,
              meta: {
                can: 'view.TmsRule',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/rules/constraints/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'deduplications/',
              name: 'tag-deduplications',
              component: SymfonyFrame,
              meta: {
                can: 'view.TmsRule',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/rules/deduplications/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'privacy/summary/',
              name: 'tag-privacy-summary',
              component: SymfonyFrame,
              meta: {
                can: 'view.TmsRule',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/rules/privacy/summary/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'privacy/constraints/',
              name: 'tag-privacy-constraints',
              component: SymfonyFrame,
              meta: {
                can: 'view.TmsRule',
                symfonyPath: to => {
                  const { containerID } = to.params;
                  const containerName = store?.getters['tms/webContainer/containerNameEncoded'];
                  return `/containers/rules/privacy/constraints/${containerID}/${containerName}`;
                },
              },
            },
            {
              path: 'web-datalayer/',
              name: 'tag-web-datalayer',
              component: SymfonyFrame,
              meta: {
                symfonyPath: '/containers/options',
              },
            },
            {
              path: 'copy-management/',
              name: 'tag-copy-management',
              component: SymfonyFrame,
              meta: {
                symfonyPath: '/options/copy/management',
              },
            },
            {
              path: 'sources-inspector/',
              name: 'tag-sources-inspector',
              component: SymfonyFrame,
              meta: {
                symfonyPath: async () => {
                  let symfonyPath = '';
                  try {
                    const {
                      data: { data: discover },
                    } = await axiosApi.get('source-inspectors', {});
                    symfonyPath = `/dashboard/load/${discover.id}/-/app/discover#/`;
                  } catch (error) {
                    console.warn('API Warning', error);
                  }
                  return symfonyPath;
                },
              },
            },
            {
              path: 'container-settings/',
              name: 'container-settings',
              component: () => import('@views/containers/container-settings.vue'),
              meta: {
                breadcrumb: () => [
                  { i18nLabel: 'menuItems.containers.containerSettings' },
                  { integrationDropdown: 'container' },
                  { changeBranch: true },
                ],
              },
            },
          ],
        },
      ],
    },
    {
      path: 'create/',
      component: Transient,
      beforeEnter(to, from, next) {
        store.commit('tms/webContainer/_clearCurrentEntity');
        store.commit('integration/integration/_clearCurrentEntity');
        next();
      },
      meta: {
        breadcrumb: null,
      },
      children: [
        {
          path: '',
          name: 'source-catalog',
          props: {
            catalogType: 'source',
          },
          component: () => import('@views/integration/catalog.vue'),
          meta: {
            titleI18n: 'source.catalog.title',
            breadcrumb: () => [
              { i18nLabel: 'source.title', routeName: 'source-overview' },
              { i18nLabel: 'common.create', routeName: 'source-catalog' },
            ],
            can: 'edit.TmsContainer|create.DmsStream',
          },
        },
        {
          path: ':id(\\d+)',
          component: NavTab,
          meta: {
            breadcrumb: null,
          },
          children: [
            {
              path: 'creation',
              name: 'source-create',
              component: () => import('@views/integration/source/creation-wizard.vue'),
              props(route) {
                const props = { ...route.params };
                props.id = Number(props.id);
                props.integrationTemplateId = Number(props.id);
                props.step = 'create';
                return props;
              },
              meta: {
                navTabHidden: true,
                breadcrumb: () => [
                  { i18nLabel: 'source.title', routeName: 'source-catalog' },
                  { i18nLabel: 'common.create', routeName: 'source-catalog' },
                  { label: store.state.integration.template.entity.label, routeName: 'source-create' },
                ],
              },
              async beforeEnter(routeTo, routeFrom, next) {
                if (routeTo.params.id) {
                  await store.dispatch('integration/template/loadCurrent', {
                    id: routeTo.params.id,
                    include: 'categories',
                  });
                }

                next();
              },
            },
            {
              path: 'description',
              name: 'source-description',
              component: () => import('@views/integration/integration-description.vue'),
              props(route) {
                const props = { ...route.params };
                props.id = Number(props.id);
                props.step = 'description';
                props.integrationType = 'source';
                return props;
              },
              meta: {
                navTabHidden: true,
                breadcrumb: () => [
                  { i18nLabel: 'source.title', routeName: 'source-catalog' },
                  { i18nLabel: 'common.create', routeName: 'source-catalog' },
                  { label: store.state.integration.template.entity.label, routeName: 'source-description' },
                ],
              },
              async beforeEnter(routeTo, routeFrom, next) {
                await store.dispatch('integration/template/loadCurrent', routeTo.params.id);

                try {
                  await getBasicText(`sources/${store.state.integration.template.entity.template}/description`);
                } catch (fetchError) {
                  if (fetchError.response.status === 404) {
                    next({ name: 'source-create', params: { id: routeTo.params.id } });
                    return;
                  }
                }
                next();
              },
            },
          ],
        },
      ],
    },
    {
      path: 'duplicate/:integrationId(\\d+)/',
      component: NavTab,
      async beforeEnter(to, from, next) {
        store.commit('tms/webContainer/_clearCurrentEntity');
        store.commit('integration/integration/_clearCurrentEntity');
        await store.dispatch('integration/integration/loadCurrent', {
          id: to.params.integrationId,
          include:
            'environment,connector,universe,updater,destinations,destinations.connector,destinations.environment,destinations.updater,destinations.sources,destinations.sources.connector',
        });
        await store.dispatch('integration/template/loadCurrent', {
          id: store.state.integration.integration.entity.connector.id,
          include: 'categories',
        });
        next();
      },
      meta: {
        breadcrumb: null,
      },
      children: [
        {
          path: '',
          name: 'source-duplicate',
          component: () => import('@views/integration/source/creation-wizard.vue'),
          props(route) {
            const props = { ...route.params };
            props.step = 'duplicate';
            return props;
          },
          meta: {
            navTabHidden: true,
            breadcrumb: () => [
              { i18nLabel: 'source.title', routeName: 'source-overview' },
              { label: store.state.integration.integration.entity.label, routeName: 'source-duplicate' },
            ],
          },
        },
      ],
    },
    {
      path: ':id(\\d+)',
      async beforeEnter(to, from, next) {
        await store.dispatch('integration/integration/loadCurrent', {
          id: to.params.id,
          include:
            'alerts,connector,connector.categories,storage_connector,universe,updater,creator,environment,destinations,destinations.connector,destinations.environment,destinations.updater,destinations.sources,destinations.sources.connector,destinations.sources.connector.categories,sources',
        });
        next();
      },
      meta: {
        breadcrumb: null,
      },
      component: Transient,
      children: [
        {
          path: 'edit',
          meta: {
            breadcrumb: null,
          },
          component: NavTab,
          children: [
            editTab('overview-tab', () => import('@views/integration/source/overview-tab.vue'), {
              async metaFn() {
                return {
                  navTabHidden: hideTab('overview-tab'),
                  titleI18n: 'source.edit.tabs.overview',
                };
              },
            }),
            editTab('input', null, {
              metaFn() {
                return {
                  navTabHidden: hideTab('input'),
                  titleI18n: 'source.edit.tabs.input',
                };
              },
            }),
            editTab('settings', () => import('@views/integration/source/source.vue'), {
              async metaFn() {
                return {
                  navTabHidden: hideTab('settings'),
                  titleI18n: 'source.edit.tabs.settings',
                };
              },
            }),
            editTab('fileSettings', () => import(`@views/integration/source/source.vue`), {
              async metaFn() {
                return {
                  navTabHidden: hideTab('fileSettings'),
                  titleI18n: 'source.edit.tabs.fileSettings',
                };
              },
            }),
            editTab('mapping', () => import(`@views/integration/source/source.vue`), {
              async metaFn() {
                return {
                  navTabHidden: hideTab('mapping'),
                  titleI18n: 'source.edit.tabs.mapping',
                };
              },
            }),
            editTab('filters', null, {
              metaFn() {
                return {
                  navTabHidden: hideTab('filters'),
                  titleI18n: 'source.edit.tabs.filters',
                };
              },
            }),
            editTab('activation', null, {
              metaFn() {
                return {
                  navTabHidden: hideTab('activation'),
                  titleI18n: 'source.edit.tabs.activation',
                };
              },
            }),
            editTab('quality', () => import('@views/integration/source/quality-tab.vue'), {
              async metaFn() {
                return {
                  navTabHidden: hideTab('quality'),
                  titleI18n: 'source.edit.tabs.quality',
                };
              },
            }),
            {
              path: 'inspector',
              name: 'source-inspector',
              component: SymfonyFrame,
              props: false,
              meta: {
                titleI18n: 'source.edit.tabs.inspector',
                breadcrumb: () => [
                  { i18nLabel: 'source.title', routeName: 'source-overview' },
                  { integrationDropdown: 'source' },
                ],
                async metaFn() {
                  return {
                    navTabHidden: hideTab('inspector'),
                  };
                },
              },
              async beforeEnter(routeTo, routeFrom, next) {
                try {
                  const {
                    data: { data: discover },
                  } = await axiosApi.get(`source-inspectors`, {});
                  routeTo.meta.symfonyPath = `/dashboard/load/${discover.id}/source_id:${store.state.integration.integration.entity.integration_key}/app/discover#/`;
                } catch (error) {
                  console.warn('API Warning', error);
                } finally {
                  next();
                }
              },
            },
          ],
        },
        {
          path: 'source-code',
          component: NavTab,
          meta: {
            breadcrumb: null,
          },
          children: [
            editTab('get-code', () => import('@views/integration/source/source.vue'), {
              async metaFn() {
                return {
                  navTabHidden: hideTab('get-code'),
                  titleI18n: 'source.sourceCode',
                };
              },
            }),
          ],
        },
      ],
    },
    {
      path: 'privacy-banners/',
      name: 'privacy-banners',
      component: SymfonyFrame,
      meta: {
        symfonyPath: '/containers/privacy/overview',
      },
    },
    {
      path: 'sources-inspector',
      name: 'sources-inspector',
      component: SymfonyFrame,
      props: false,
      async beforeEnter(routeTo, routeFrom, next) {
        let symfonyPath = '';
        try {
          const {
            data: { data: discover },
          } = await axiosApi.get('source-inspectors', {});
          symfonyPath = `/dashboard/load/${discover.id}/-/app/discover#/`;
          routeTo.meta.symfonyPath = symfonyPath;
        } catch (error) {
          console.warn('API Warning', error);
        } finally {
          next();
        }
      },
      meta: {
        breadcrumb: () => [{ i18nLabel: 'source.liveEventInspectorAllSource', routeName: 'sources-inspector' }],
        showBreadcrumb: true,
      },
    },
    {
      path: 'trafficking/',
      name: 'trafficking',
      component: SymfonyFrame,
      meta: {
        symfonyPath: '/campaign/trafficking',
      },
    },
    {
      path: 'data/:id',
      name: 'data-source-edit-v7',
      component: SymfonyFrame,
      meta: {
        symfonyPath: to => {
          const { id } = to.params;
          return `/data/connect/edit/${id}`;
        },
      },
    },
  ],
};
